import { useEffect, useState } from "react";
import { api } from "../lib/api";
const STATUS_STYLES = {
    OPEN: { background: "var(--st-info-bg)", color: "var(--st-info)" },
    IN_PROGRESS: { background: "var(--st-pending-bg)", color: "var(--st-pending)" },
    COMPLETED: { background: "var(--st-open-bg)", color: "var(--st-open)" },
    OVERDUE: { background: "var(--st-risk-bg)", color: "var(--st-risk)" },
    CANCELLED: { background: "var(--surface-2)", color: "var(--ink-faint)" },
};
export function FollowUpsPanel({ caseId, currentUserId }) {
    const [items, setItems] = useState([]);
    const [coordinators, setCoordinators] = useState([]);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [actionRequired, setActionRequired] = useState("");
    const [followUpType, setFollowUpType] = useState("Call");
    const [dueDate, setDueDate] = useState("");
    const [priority, setPriority] = useState("MEDIUM");
    const [assignedTo, setAssignedTo] = useState(currentUserId);
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    function load() {
        api
            .get(`/cases/${caseId}/follow-ups`)
            .then((r) => setItems(r.items))
            .catch(() => setError("Couldn't load follow-ups."));
    }
    useEffect(load, [caseId]);
    useEffect(() => {
        api.get("/users").then((r) => {
            setCoordinators(r.items);
            // If the caller isn't a card holder, default to the first one
            // rather than leaving assignedTo pointed at an unassignable id.
            if (!r.items.some((u) => u.id === currentUserId) && r.items[0])
                setAssignedTo(r.items[0].id);
        });
    }, [currentUserId]);
    async function create() {
        const errs = {};
        if (!actionRequired.trim())
            errs.actionRequired = "Describe the action required.";
        if (!dueDate)
            errs.dueDate = "Due date is required.";
        setFieldErrors(errs);
        if (Object.keys(errs).length)
            return;
        setSaving(true);
        setError(null);
        try {
            await api.post(`/cases/${caseId}/follow-ups`, {
                followUpType,
                actionRequired,
                assignedTo,
                followUpDate: new Date().toISOString(),
                dueDate: new Date(dueDate).toISOString(),
                priority,
            });
            setActionRequired("");
            setDueDate("");
            setFieldErrors({});
            setShowForm(false);
            load();
        }
        catch {
            setError("Couldn't create the follow-up.");
        }
        finally {
            setSaving(false);
        }
    }
    async function setStatus(id, status) {
        try {
            await api.put(`/follow-ups/${id}`, { status });
            load();
        }
        catch {
            setError("Couldn't update status.");
        }
    }
    return (<div>
      {error && <div style={{ color: "var(--st-risk)", fontSize: "0.85rem", marginBottom: 12 }}>{error}</div>}

      {!showForm ? (<button onClick={() => setShowForm(true)} style={btnStyle}>
          + Add follow-up
        </button>) : (<div style={{ border: "1px solid var(--line-soft)", borderRadius: 8, padding: 14, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={followUpType} onChange={(e) => setFollowUpType(e.target.value)} style={inputStyle}>
            <option>Call</option>
            <option>Visit</option>
            <option>Court Hearing</option>
            <option>Document Collection</option>
          </select>
          <div style={{ flex: 1, minWidth: 160 }}>
            <input placeholder="Action required" value={actionRequired} onChange={(e) => setActionRequired(e.target.value)} style={{ ...inputStyle, width: "100%" }}/>
            {fieldErrors.actionRequired && <div style={errText}>{fieldErrors.actionRequired}</div>}
          </div>
          <div>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={inputStyle}/>
            {fieldErrors.dueDate && <div style={errText}>{fieldErrors.dueDate}</div>}
          </div>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inputStyle}>
            <option>LOW</option>
            <option>MEDIUM</option>
            <option>HIGH</option>
          </select>
          <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} style={inputStyle}>
            {coordinators.map((u) => (<option key={u.id} value={u.id}>
                {u.id === currentUserId ? `${u.name} (me)` : u.name}
              </option>))}
          </select>
          <button onClick={create} disabled={saving} style={btnStyle}>
            Save
          </button>
          <button onClick={() => setShowForm(false)} style={cancelBtn}>
            Cancel
          </button>
        </div>)}

      {items.map((f) => (<div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderTop: "1px solid var(--line-soft)" }}>
          <div>
            <div style={{ fontSize: "0.86rem", fontWeight: 600 }}>{f.actionRequired}</div>
            <div className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)" }}>
              {f.followUpType} · due {new Date(f.dueDate).toLocaleDateString()} · {f.priority}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span className="mono" style={{ ...STATUS_STYLES[f.status], fontSize: "0.7rem", fontWeight: 600, padding: "3px 9px", borderRadius: 5 }}>
              {f.status}
            </span>
            {(f.status === "OPEN" || f.status === "IN_PROGRESS" || f.status === "OVERDUE") && (<select value="" onChange={(e) => e.target.value && setStatus(f.id, e.target.value)} style={{ ...inputStyle, fontSize: "0.75rem" }}>
                <option value="">Change status…</option>
                {f.status !== "IN_PROGRESS" && <option value="IN_PROGRESS">In Progress</option>}
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>)}
          </div>
        </div>))}
      {items.length === 0 && !error && <div style={{ color: "var(--ink-faint)", fontSize: "0.85rem" }}>No follow-ups for this case.</div>}
    </div>);
}
const inputStyle = {
    border: "1px solid var(--line)",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: "0.82rem",
    fontFamily: "inherit",
};
const btnStyle = {
    background: "var(--brand)",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.82rem",
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
};
const errText = { color: "var(--st-risk)", fontSize: "0.7rem", marginTop: 3 };
const cancelBtn = {
    background: "transparent",
    border: "1px solid var(--line)",
    color: "var(--ink-soft)",
    fontWeight: 600,
    fontSize: "0.82rem",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
};
